<template>
    <div>
        <!-- Search + Filters -->
        <div v-if="!selectedDocument" class="document-search-list">
            <div class="filter-bar">
                <input
                    v-model="searchText"
                    type="text"
                    class="search-input"
                    :placeholder="$t('forms.view_document.fields.search.placeholder')"
                    @input="onSearch"
                />
                <common-icon name="search" size="small" @click="getDocuments(doc)"></common-icon>
            </div>
            <div class="tab-filter">
                <select-input
                    class="schedule-apply"
                    :placeholder="$t('forms.view_document.fields.type.placeholder')"
                    :hasAutofocus="true"
                    :options="typeOptions"
                    :value="recordTypeId"
                    @change="changeTypeId($event)"
                ></select-input>

                <select-input
                    class="schedule-apply"
                    :placeholder="$t('forms.view_document.fields.source.placeholder')"
                    :hasAutofocus="true"
                    :options="sourceOptions"
                    :value="sourceId"
                    @change="changeSourceId($event)"
                ></select-input>
            </div>
        </div>
        <common-buttons v-if="!selectedDocument" align="left" class="download-print-btns">
            <common-button
                :label="$t('common.images.download')"
                type="secondary"
                :size="'tiny'"
                :disabled="!hasSelectedDocuments"
                @click="downloadSelected"
            ></common-button>
            <common-button
                :label="$t('common.images.print')"
                type="secondary"
                :size="'tiny'"
                :disabled="!hasSelectedDocuments"
                @click="printSelected"
            ></common-button>
        </common-buttons>

        <!-- Document List -->
        <div v-if="!selectedDocument" class="document-list">
            <template v-if="isLoading">
                <div class="loading-spinner">
                    <span>{{ $t('modals.import_contacts_modal.loading') }}</span>
                    <loading-spin></loading-spin>
                </div>
            </template>
            <div v-else>
                <h3 class="card-title">
                    {{ $t('modals.patient_profile.profile.detail_form.card_head', { count: totalCount }) }}
                </h3>
                <div v-for="doc in documents" :key="doc.id" class="document-card">
                    <div class="doc-icon">{{ doc.icon }}</div>
                    <div class="doc-info">
                        <div class="doc-title">{{ doc.title }}</div>
                        <div class="doc-meta">
                            <span>{{ doc.date }}</span>
                            ·
                            <span>{{ doc.provider }}</span>
                            ·
                            <span>{{ doc.size }}</span>
                        </div>
                    </div>

                    <div class="doc-actions">
                        <div class="doc-tag" @click="viewDocument(doc.id)">{{ doc.type }}</div>
                        <common-icon name="view" size="small" @click="viewDocument(doc.id)"></common-icon>
                        <button>
                            <common-icon name="download" size="small" @click="viewDocument(doc.id)"></common-icon>
                        </button>
                        <div>
                            <input type="checkbox" class="input-checkbox" :checked="doc.selected" @change="toggleDocument(doc)" />
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <common-buttons v-if="!selectedDocument && !isLoading && documents.length > 0 && documents.length < totalCount">
            <common-button
                :label="$t('pages.physicians.show.buttons.view_more')"
                type="primary"
                @click="viewMore"
            ></common-button>
        </common-buttons>

        <!-- Document Detail View -->
        <div v-if="selectedDocument" class="detail-view">
            <div class="detail-header">
                <h3>{{ selectedDocument?.fileName }}</h3>
                <button class="btn">
                    <common-icon name="download" size="small"></common-icon>
                    {{ $t('modals.patient_profile.profile.detail_form.button.title') }}
                </button>
            </div>

            <div class="detail-body">
                <div class="preview-box">
                    <template v-if="isPreviewLoading">
                        <div class="loading-spinner">
                            <span>{{ $t('modals.import_contacts_modal.loading') }}</span>
                            <loading-spin></loading-spin>
                        </div>
                    </template>
                    <iframe v-else-if="isPdf && fileBlobUrl" :src="fileBlobUrl" class="pdf-viewer" frameborder="0"></iframe>
                    <img v-else-if="isImage && fileBlobUrl" :src="fileBlobUrl" class="image-viewer" />
                    <div v-else class="preview-placeholder">
                        <common-icon name="file" size="extra-large"></common-icon>
                        <p>{{ $t('modals.patient_profile.profile.detail_form.card_heading') }}</p>
                        <span>{{ selectedDocument.fileName }} • {{ formatFileSize(selectedDocument.size) }}</span>
                        <a :href="fileBlobUrl" target="_blank">
                            {{ $t('common.images.download') }}
                        </a>
                    </div>
                </div>

                <div class="doc-details">
                    <h4>{{ $t('modals.patient_profile.profile.detail_form.document_detail.title') }}</h4>
                    <p>
                        <span class="data">{{ $t('modals.patient_profile.profile.detail_form.document_detail.type') }}</span>
                        <span class="doc-tag">{{ selectedDocument?.recordType?.name }}</span>
                    </p>
                    <p>
                        <span class="data">{{ $t('modals.patient_profile.profile.detail_form.document_detail.date') }}</span>
                        {{ dateFormate(selectedDocument.date) }}
                    </p>
                    <p>
                        <span class="data">{{ $t('modals.patient_profile.profile.detail_form.document_detail.provider') }}</span>
                        {{ selectedDocument?.source?.name }}
                    </p>
                    <p>
                        <span class="data">{{ $t('modals.patient_profile.profile.detail_form.document_detail.size') }}</span>
                        {{ formatFileSize(selectedDocument.size) }}
                    </p>
                    <h4>{{ $t('modals.patient_profile.profile.detail_form.document_detail.description') }}</h4>
                    <p>{{ selectedDocument?.note }}</p>

                    <button class="back-btn" @click="selectedDocument = null">
                        {{ $t('modals.patient_profile.profile.detail_form.document_detail.button.title') }}
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
    import FormMixin from 'mixins/FormMixin'
    import HandleRequestMixin from 'mixins/HandleRequestMixin'
    import PatientAPI from 'api/PatientAPI'
    import FileMixin from 'mixins/FileMixin'
    import { Circle9 } from 'vue-loading-spinner'
    import AuthService from 'services/AuthService'

    export default {
        mixins: [FormMixin, HandleRequestMixin, FileMixin],
        components: {
            'loading-spin': Circle9,
        },
        props: {
            documentOptions: { type: Array, default: () => [] },
        },
        data() {
            return {
                patientId: null,
                selectedDocument: null,
                recordTypeId: 0,
                sourceId: 0,
                sourceOptions: [],
                documents: [],
                totalCount: 0,
                searchText: '',
                offset: 0,
                limit: 10,
                isLoading: false,
                images: [],
                selectedDocuments: [],
                fileBlobUrl: null,
                fileExtension: null,
                isPreviewLoading: false,
            }
        },

        computed: {
            isPdf() {
                return this.fileExtension === 'pdf'
            },
            isImage() {
                return ['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(this.fileExtension)
            },
            typeOptions() {
                return [{ label: this.$t('modals.patient_profile.profile.type.text'), value: 0 }, ...this.documentOptions]
            },
            hasSelectedDocuments() {
                return this.selectedDocuments.length > 0
            },
        },
        methods: {
            downloadSelected() {
                this.selectedDocuments.forEach((doc) => {
                    this.downloadFile(doc.url, doc.title)
                })
            },

            printSelected() {
                this.selectedDocuments.forEach((doc) => {
                    const routeData = this.$router.resolve({
                        name: 'image.preview',
                        query: { url: doc.url },
                    })
                    window.open(routeData.href, '_blank')
                })
            },
            toggleDocument(doc) {
                doc.selected = !doc.selected
                if (doc.selected) {
                    if (!this.selectedDocuments.find((d) => d.id === doc.id)) {
                        this.selectedDocuments.push(doc)
                    }
                } else {
                    this.selectedDocuments = this.selectedDocuments.filter((d) => d.id !== doc.id)
                }
            },
            changeTypeId(val) {
                this.recordTypeId = Number(val)
                this.offset = 0
                this.documents = []
                this.getDocuments()
            },

            changeSourceId(val) {
                this.sourceId = Number(val)
                this.offset = 0
                this.documents = []
                this.getDocuments()
            },

            getSources() {
                PatientAPI.getSources(this.patientId).then((res) => {
                    this.sourceOptions = [
                        { label: this.$t('modals.patient_profile.profile.source.text'), value: 0 },
                        ...res.map((item) => ({
                            label: item.name,
                            value: item.id,
                        })),
                    ]
                })
            },

            getDocuments(append = false) {
                this.isLoading = true
                PatientAPI.getDocuments(this.patientId, {
                    limit: this.limit,
                    offset: this.offset,
                    recordTypeId: this.recordTypeId || undefined,
                    sourceId: this.sourceId || undefined,
                    search: this.searchText || undefined,
                })
                    .then((res) => {
                        this.totalCount = Number(res._headers['x-total-count'])
                        const mapped = res.map((item) => {
                            return {
                                id: item.id,
                                title: item.fileName,
                                date: new Date(item.date).toLocaleDateString('en-GB'),
                                provider: item?.source?.name || 'Unknown',
                                size: this.formatFileSize(item.size),
                                type: item?.recordType?.name || 'Unknown',
                                icon: this.getDocIcon(item?.recordType?.id),
                                description: item.note || 'No description available',
                                url: item.url,
                            }
                        })
                        if (append) {
                            this.documents = [...this.documents, ...mapped]
                        } else {
                            this.documents = mapped
                        }
                    })
                    .finally(() => {
                        this.isLoading = false
                    })
            },

            formatFileSize(kb) {
                if (!kb) return '0 KB'
                if (kb < 1024) return `${kb} KB`
                return `${(kb / 1024).toFixed(1)} MB`
            },

            getDocIcon(type) {
                switch (type) {
                    case 1:
                        return '🧪'
                    case 2:
                        return '📷'
                    case 3:
                        return '👩‍⚕️'
                    case 4:
                        return '💊'
                    default:
                        return '📄'
                }
            },

            onSearch() {
                clearTimeout(this._searchDelay)
                this._searchDelay = setTimeout(() => {
                    this.offset = 0
                    this.documents = []
                    this.getDocuments()
                }, 400)
            },

            viewMore() {
                this.offset = this.offset + this.limit
                this.getDocuments(true)
            },

            dateFormate(date) {
                return new Date(date).toLocaleDateString('en-GB')
            },

            getExtension(url) {
                const source = url
                if (!source) return ''
                return source.split('.').pop().toLowerCase()
            },

            viewDocument(documentId) {
                this.fileBlobUrl = null
                this.fileExtension = null
                this.isPreviewLoading = true

                PatientAPI.getSingleDocument(this.patientId, documentId).then((res) => {
                    this.selectedDocument = res
                    this.fetchFile(res)
                })
            },
            fetchFile(document) {
                if (this.fileBlobUrl) {
                    URL.revokeObjectURL(this.fileBlobUrl)
                }

                this.fileBlobUrl = null
                this.fileExtension = this.getExtension(document.url)
                this.isPreviewLoading = true

                fetch(document.url, {
                    headers: {
                        Authorization: `Bearer ${AuthService.getToken()}`,
                    },
                })
                    .then((res) => res.blob())
                    .then((blob) => {
                        this.fileBlobUrl = URL.createObjectURL(blob)
                    })
                    .catch(() => {
                        this.fileBlobUrl = null
                    })
                    .finally(() => {
                        this.isPreviewLoading = false
                    })
            },
        },
        mounted() {
            this.patientId = this.$route.params.id
            this.getSources()
            this.getDocuments()
        },
    }
</script>

<style scoped>
    .loading-spinner {
        text-align: center;
        font-size: 16px;

        .spinner {
            margin: 0 auto;
            zoom: 150%;
        }
    }
    .document-search-list {
        display: flex;
        border-bottom: 1px solid #ddd;
        padding-bottom: 15px;
        margin-bottom: 20px;
    }
    .filter-bar {
        display: flex;
        gap: 10px;
        position: relative;
        width: 100%;
    }
    .search-input {
        flex: 1;
        padding: 8px 29px;
        border-radius: 6px;
        border: 1px solid #f5f5f5;
        font-size: 12px;
        background-color: #f5f5f5;
        height: 51px;
    }
    .schedule-apply {
        width: 200px;
        margin-left: 20px;
        padding: 0;
    }
    .download-print-btns {
        margin-bottom: 10px;
    }
    .input-checkbox {
        cursor: pointer;
        width: 14px;
        height: 14px;
        margin-left: 18px;
    }
    .document-list {
        background-color: #fff;
        padding: 16px;
        border-radius: 8px;
        border: 1px solid rgba(0, 0, 0, 0.05);
        margin-bottom: 20px;
    }
    .card-title {
        font-size: 18px;
        font-weight: 500;
        margin: 10px 0;
        color: oklch(0.145 0 0);
    }
    .document-card {
        display: flex;
        align-items: center;
        justify-content: space-between;
        background: white;
        padding: 12px;
        border-radius: 8px;
        margin-bottom: 10px;
        border: 1px solid rgba(0, 0, 0, 0.1);
    }
    .document-card:hover {
        background-color: oklch(0.985 0.002 247.839);
    }
    .doc-icon {
        font-size: 24px;
    }
    .doc-info {
        flex: 1;
        margin-left: 10px;
    }
    .doc-title {
        font-weight: 500;
        font-size: 14px;
        color: oklch(0.145 0 0);
    }
    .doc-meta {
        font-size: 12px;
        color: gray;
    }
    .doc-tag {
        background: oklch(0.95 0.0058 264.53);
        padding: 4px 8px;
        border-radius: 8px;
        font-size: 12px;
        font-weight: 500;
        color: #030213;
        margin-right: 18px;
    }
    .doc-actions button {
        margin-left: 20px;
        border: none;
        background: none;
        cursor: pointer;
    }
    .doc-actions {
        display: flex;
    }
    .detail-view {
        background: white;
        padding: 20px;
        border-radius: 12px;
        box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1);
    }
    .detail-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
    }
    .detail-header h3 {
        font-weight: 500;
        font-size: 14px;
        color: oklch(0.145 0 0);
    }
    .btn {
        padding: 4px 12px;
        color: oklch(25.723% 0.00149 16.173);
        border-radius: 8px;
        font-weight: 500;
        font-size: 13px;
        border: 1px solid #d1d5db;
        background: transparent;
        cursor: pointer;
        display: flex;
        align-items: center;
        justify-content: center;
    }
    .detail-body {
        display: flex;
        gap: 20px;
        margin-top: 20px;
    }
    .preview-box {
        flex: 2;
        border: 1px solid #ddd;
        border-radius: 8px;
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 40px;
        background: #f8f9fa;
        text-align: center;
    }
    .preview-placeholder p {
        font-size: 16px;
        font-weight: 400;
        margin: 8px 10px;
    }
    .preview-placeholder span {
        font-size: 12px;
        font-weight: 400;
    }
    .doc-details {
        flex: 1;
    }
    .doc-details h4 {
        color: oklch(0.145 0 0);
        font-size: 16px;
        font-weight: 500;
        margin-bottom: 8px;
    }
    .doc-details p {
        color: oklch(0.145 0 0);
        font-size: 12px;
        margin-bottom: 9px;
    }
    .data {
        margin-right: 8px;
        color: oklch(0.446 0.03 256.802);
    }
    .status-tag {
        background: black;
        color: white;
        padding: 2px 6px;
        border-radius: 6px;
        font-size: 12px;
    }
    .back-btn {
        padding: 4px 12px;
        color: oklch(16.842% 0.00002 271.152);
        border-radius: 8px;
        font-weight: 600;
        font-size: 13px;
        border: 1px solid #d1d5db;
        background: transparent;
        cursor: pointer;
        width: 100%;
        display: flex;
        align-items: center;
        justify-content: center;
    }
    .tab-filter {
        display: flex;
    }
    .filter-bar i {
        position: absolute;
        top: 15px;
        left: 7px;
    }
    @media screen and (max-width: 600px) {
        .document-search-list,
        .document-card,
        .detail-body {
            display: block !important;
        }
        .schedule-apply {
            margin-left: 3px !important;
            margin-top: 7px !important;
        }
    }
    .pdf-viewer {
        width: 100%;
        height: 650px;
        border: 1px solid #ddd;
        border-radius: 6px;
    }

    .image-viewer {
        max-width: 100%;
        max-height: 650px;
        object-fit: contain;
        border-radius: 6px;
    }
</style>
